import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  FormHelperText,
  Snackbar,
  Alert,
  Grid,
  Chip
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TramOutlinedIcon from '@mui/icons-material/TramOutlined';
import ElectricRickshawOutlinedIcon from '@mui/icons-material/ElectricRickshawOutlined';
import SailingOutlinedIcon from '@mui/icons-material/SailingOutlined';
import AddBoxIcon from '@mui/icons-material/AddBox';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { makeStyles } from '@mui/styles';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { LoadingButton } from '@mui/lab';
import { CreateNewVehicle, UpdateVehicleDetailsById } from '../../../network/Vehicles';

// ─────────────────────────────── Constants ───────────────────────────────────

const STEPS = [
  { id: 'identity', label: 'Identity' },
  { id: 'config', label: 'Config' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'hardware', label: 'Hardware' }
];

const FUEL_TYPES = ['diesel', 'petrol', 'cng', 'lng', 'electric', 'hybrid', 'other'];
const VOICE_BOX_ALERT_LANGUAGES = [
  'English',
  'Hindi',
  'Marathi',
  'Kannada',
  'Telugu',
  'Tamil',
  'Bengali'
];
const GPS_DEVICES = [
  { id: 'gps-hardcoded-1', name: 'GPS Tracker 1' },
  { id: 'gps-hardcoded-2', name: 'GPS Tracker 2' },
  { id: 'gps-hardcoded-3', name: 'GPS Tracker 3' },
  { id: 'gps-hardcoded-4', name: 'GPS Tracker 4' },
  { id: 'gps-hardcoded-5', name: 'GPS Tracker 5' }
];
const DASHCAM_DEVICES = [
  { id: 'dashcam-hardcoded-1', name: 'Dashcam AI 1' },
  { id: 'dashcam-hardcoded-2', name: 'Dashcam AI 2' },
  { id: 'dashcam-hardcoded-3', name: 'Dashcam AI 3' },
  { id: 'dashcam-hardcoded-4', name: 'Dashcam AI 4' },
  { id: 'dashcam-hardcoded-5', name: 'Dashcam AI 5' }
];
const LOAD_TYPES = ['Liquid', 'Dry Bulk', 'General Cargo', 'Hazardous', 'Refrigerated', 'Other'];

// ─────────────────────────────── Styles ──────────────────────────────────────

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '85%', md: '52%' },
  maxWidth: { xs: '85%', md: '52%' },
  bgcolor: 'background.paper',
  p: 2.5,
  borderRadius: 1,
  minHeight: '30vh',
  maxHeight: '90vh',
  overflow: 'auto'
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: { fontSize: '12px' },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  },
  certCard: {
    background: '#f0f4f8',
    borderRadius: '12px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  }
}));

// ─────────────────────────────── Timeline ────────────────────────────────────

function AssetTimeline({ currentStep }) {
  const currentIdx = STEPS.findIndex((s) => s.id === currentStep);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
      {STEPS.map((step, idx) => (
        <React.Fragment key={step.id}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52 }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: idx <= currentIdx ? '#1976d2' : '#e5e7eb',
                color: idx <= currentIdx ? '#fff' : '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                boxShadow: idx === currentIdx ? '0 0 0 4px rgba(25,118,210,0.18)' : 'none',
                transition: 'all 0.3s'
              }}
            >
              {idx < currentIdx ? <CheckIcon sx={{ fontSize: 15 }} /> : idx + 1}
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: idx === currentIdx ? 700 : 500,
                mt: 0.6,
                color: idx <= currentIdx ? '#1976d2' : '#9ca3af',
                transition: 'color 0.3s',
                whiteSpace: 'nowrap'
              }}
            >
              {step.label}
            </Typography>
          </Box>
          {idx < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 2,
                backgroundColor: idx < currentIdx ? '#1976d2' : '#e5e7eb',
                mt: 1.875,
                mx: 0.5,
                borderRadius: 1,
                transition: 'background-color 0.3s'
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

// ─────────────────────────────── Cert Card ───────────────────────────────────

function CertCard({ title, fields }) {
  const [values, setValues] = useState(fields.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {}));
  return (
    <Box
      sx={{
        background: '#f0f4f8',
        borderRadius: '12px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#64748b'
        }}
      >
        {title}
      </Typography>
      {fields.map((f) => (
        <TextField
          key={f.key}
          fullWidth
          placeholder={f.placeholder}
          type={f.type || 'text'}
          size="small"
          value={values[f.key]}
          onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
          sx={{
            background: '#fff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            '& .MuiOutlinedInput-input': { fontSize: '13px', padding: '10px 14px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
          }}
          InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
        />
      ))}
    </Box>
  );
}

// ─────────────────────────────── Main Component ──────────────────────────────

export default function AddVehicleModal({
  openAssignedModal,
  onAction,
  vehicleData,
  setIsAddVehicleModalOpen,
  setVehicleData,
  fleets = [],
  drivers = [],
  transporters = [],
  gpsDevices = [],
  dashcamDevices = []
}) {
  const classes = useStyles();
  const [currentStep, setCurrentStep] = useState('identity');
  const fallbackFleetId = fleets?.[0]?.id || '';
  const fallbackTransporterId = transporters?.[0]?.id || '';
  const fallbackDriverId = drivers?.[0]?.id || '';
  const normalizedGpsDevices =
    Array.isArray(gpsDevices) && gpsDevices.length
      ? gpsDevices.map((d, idx) =>
          typeof d === 'string'
            ? { id: d, name: d }
            : {
                id: d?.id || d?.deviceId || `gps-live-${idx + 1}`,
                name: d?.name || d?.deviceName || `GPS Tracker ${idx + 1}`
              }
        )
      : GPS_DEVICES;
  const normalizedDashcamDevices =
    Array.isArray(dashcamDevices) && dashcamDevices.length
      ? dashcamDevices.map((d, idx) =>
          typeof d === 'string'
            ? { id: d, name: d }
            : {
                id: d?.id || d?.deviceId || `dashcam-live-${idx + 1}`,
                name: d?.name || d?.deviceName || `Dashcam AI ${idx + 1}`
              }
        )
      : DASHCAM_DEVICES;

  // Chambers state — lives outside formik since it's local config UI
  const [totalCapacity, setTotalCapacity] = useState('');
  const [chambers, setChambers] = useState([{ loadCapacity: '', loadType: '' }]);
  const [chamberError, setChamberError] = useState('');
  const [chamberCount, setChamberCount] = useState(1);

  // Hardware selections
  const [gpsDevice, setGpsDevice] = useState('');
  const [dashcamDevice, setDashcamDevice] = useState('');

  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    msg: '',
    variant: 'filled',
    severity: ''
  });

  const handleSnackBarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setAlertMetas((p) => ({ ...p, snackBarOpened: false }));
  };

  // Sync device selections when editing vehicle
  useEffect(() => {
    setGpsDevice(vehicleData?.gpsDeviceId || '');
    setDashcamDevice(vehicleData?.dashcamDeviceId || '');
  }, [vehicleData?.gpsDeviceId, vehicleData?.dashcamDeviceId]);

  useEffect(() => {
    if (!openAssignedModal) return;
    if (!gpsDevice && normalizedGpsDevices[0]?.id) setGpsDevice(normalizedGpsDevices[0].id);
    if (!dashcamDevice && normalizedDashcamDevices[0]?.id)
      setDashcamDevice(normalizedDashcamDevices[0].id);
  }, [openAssignedModal, gpsDevice, dashcamDevice, normalizedGpsDevices, normalizedDashcamDevices]);

  const VehicleSchema = Yup.object().shape({
    vehicleName: Yup.string().required('Required'),
    registrationNo: vehicleData?.id
      ? Yup.string().notRequired()
      : Yup.string()
          .required('Required')
          .matches(/^(?=.*[A-Za-z])(?=.*\d).+$/, 'Invalid registration number format'),
    manufacturerYear: Yup.string().required('Required'),
    make: Yup.string().required('Required'),
    fuelType: Yup.string().required('Required'),
    vehicleType: Yup.string().required('Required'),
    assignedDriver: Yup.string().notRequired(),
    fleet: Yup.string().notRequired(),
    transporter: Yup.string().notRequired(),
    vehicleSpeed: Yup.number().typeError('Must be a number').required('Required'),
    chassisNo: Yup.string().required('Required'),
    engineNo: Yup.string().required('Required'),
    chambers: Yup.array().of(
      Yup.object().shape({
        loadCapacity: Yup.number().required('Required'),
        loadType: Yup.string().required('Required')
      })
    )
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicleName: vehicleData?.name || '',
      registrationNo: vehicleData?.registrationNo || '',
      vehicleType: vehicleData?.type || '',
      make: vehicleData?.make || '',
      fuelType: vehicleData?.fuelType || '',
      manufacturerYear: vehicleData?.manufacturerYear || '',
      chassisNo: vehicleData?.chassisNo || '',
      engineNo: vehicleData?.engineNo || '',
      fleet: vehicleData?.fleetId || fallbackFleetId,
      transporter: vehicleData?.transporterId || fallbackTransporterId,
      assignedDriver: vehicleData?.driverId || fallbackDriverId,
      vehicleSpeed: vehicleData?.vehicleSpeed || 80,
      vehicleAbout: vehicleData?.about || '',
      voiceBoxAlertLanguage: vehicleData?.voiceBoxAlertLanguage || 'English'
    },
    validationSchema: VehicleSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        name: values.vehicleName,
        type: values.vehicleType,
        registrationNo: values.registrationNo,
        manufacturer: values.make,
        fuelType: values.fuelType,
        manufacturerYear: values.manufacturerYear,
        chassisNo: values.chassisNo,
        engineNo: values.engineNo,
        about: values.vehicleAbout,
        vehicle_speed: parseInt(values.vehicleSpeed, 10),
        fleetId: '797f8ff3-35f2-43eb-bf15-c4ebf293aa99',
        transporterId: '3359c73f-aa2d-499a-a643-f11e345771c3',
        driverId: '91ac5cd0-70f1-4d22-8c43-e3329e776cdc',
        chamberConfig: {
          totalCapacity,
          chambers: chambers.map((c, i) => ({
            chamber: i + 1,
            loadCapacity: c.loadCapacity,
            loadType: c.loadType
          }))
        },
        gpsId: gpsDevice || normalizedGpsDevices[0]?.id || '',
        dashCamId: dashcamDevice || normalizedDashcamDevices[0]?.id || '',
        chamberCount,
        model: 'TATA',
        capacity: 12000
        // compliance is intentionally excluded from payload
      };
      CreateNewVehicleData(payload);
      resetForm();
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleClose = () => {
    setVehicleData(null);
    formik.resetForm();
    setCurrentStep('identity');
    setChambers([{ loadCapacity: '', loadType: '' }]);
    setTotalCapacity('');
    setChamberError('');
    setGpsDevice('');
    setDashcamDevice('');
    setIsAddVehicleModalOpen(false);
  };

  const handleNext = async () => {
    if (currentStep === 'identity') {
      const errs = await formik.validateForm();
      const hasIdentityErrors =
        errs.vehicleName ||
        errs.vehicleType ||
        (!vehicleData?.id && errs.registrationNo) ||
        errs.make ||
        errs.manufacturerYear ||
        errs.fuelType ||
        errs.chassisNo ||
        errs.engineNo ||
        errs.vehicleSpeed;
      if (hasIdentityErrors) {
        formik.setTouched({
          ...formik.touched,
          vehicleName: true,
          vehicleType: true,
          registrationNo: true,
          make: true,
          manufacturerYear: true,
          fuelType: true,
          chassisNo: true,
          engineNo: true,
          vehicleSpeed: true
        });
        return;
      }
      setCurrentStep('config');
    } else if (currentStep === 'config') {
      // Validate chambers sum
      const sum = chambers.reduce((acc, c) => acc + parseFloat(c.loadCapacity || 0), 0);
      const total = parseFloat(totalCapacity || 0);
      if (total > 0 && Math.abs(sum - total) > 0.01) {
        setChamberError(`Chamber capacities sum (${sum}) must equal total capacity (${total})`);
        return;
      }
      setChamberError('');
      setCurrentStep('compliance');
    } else if (currentStep === 'compliance') {
      setCurrentStep('hardware');
    }
  };

  const handleBack = () => {
    if (currentStep === 'config') setCurrentStep('identity');
    else if (currentStep === 'compliance') setCurrentStep('config');
    else if (currentStep === 'hardware') setCurrentStep('compliance');
  };

  const CreateNewVehicleData = async (payload) => {
    try {
      const res = vehicleData?.id
        ? await UpdateVehicleDetailsById(payload, vehicleData?.id)
        : await CreateNewVehicle(payload);
      if (res) {
        setAlertMetas({
          severity: 'success',
          snackBarOpened: true,
          variant: 'filled',
          msg: vehicleData?.id ? 'Vehicle Updated Successfully!' : 'Vehicle Added Successfully!'
        });
        setTimeout(() => setAlertMetas((p) => ({ ...p, snackBarOpened: false, msg: '' })), 4000);
        onAction();
      }
    } catch (error) {
      setAlertMetas({
        severity: 'error',
        snackBarOpened: true,
        variant: 'filled',
        msg: error?.response?.data?.message || 'Something went wrong'
      });
      setTimeout(() => setAlertMetas((p) => ({ ...p, snackBarOpened: false, msg: '' })), 4000);
    } finally {
      handleClose();
    }
  };

  // ✅ Sync chambers when count changes
  const handleChamberCountChange = (value) => {
    const count = Math.max(1, Number(value) || 1);

    // update chamberCount state (NEW)
    setChamberCount(count);

    // adjust chambers array length (KEEP EXISTING STRUCTURE)
    setChambers((prev) => {
      const updated = [...prev];

      if (count > updated.length) {
        // add chambers
        for (let i = updated.length; i < count; i += 1) {
          updated.push({ loadCapacity: '', loadType: '' });
        }
      } else if (count < updated.length) {
        // remove extra chambers
        updated.length = count;
      }

      return updated;
    });

    setChamberError('');
  };

  // ── Shared sx ─────────────────────────────────────────────────────────────
  const fieldSx = {
    '& .MuiOutlinedInput-input': {
      fontSize: { xs: '12px', sm: '13px' },
      padding: { xs: '12px 8px', sm: '14px' }
    }
  };
  const selectSx = {
    fontSize: { xs: '12px', sm: '13px' },
    '& .MuiOutlinedInput-input': { padding: { xs: '12px 8px', sm: '14px' } }
  };
  const labelSx = { fontSize: { xs: '12px', sm: '13px' } };

  // eslint-disable-next-line react/no-unstable-nested-components
  function FieldLabel({ children }) {
    return (
      <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: 500 }}>
        {children}
      </Typography>
    );
  }

  // ── Chamber helpers ───────────────────────────────────────────────────────

  const updateChamber = (idx, field, val) => {
    setChambers((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));
    setChamberError('');
  };

  const chamberSum = chambers.reduce((acc, c) => acc + parseFloat(c.loadCapacity || 0), 0);
  const totalNum = parseFloat(totalCapacity || 0);
  const capacityOk = totalNum > 0 && Math.abs(chamberSum - totalNum) <= 0.01;

  const isOverCapacity = chamberSum > totalNum && totalNum > 0;

  let bgColor = '#f1f5f9';
  let textColor = '#64748b';
  let borderColor = '#e2e8f0';

  if (capacityOk) {
    bgColor = '#dcfce7';
    textColor = '#16a34a';
    borderColor = '#bbf7d0';
  } else if (isOverCapacity) {
    bgColor = '#fee2e2';
    textColor = '#dc2626';
    borderColor = '#fecaca';
  }

  // ── Step Content ──────────────────────────────────────────────────────────
  const renderStepContent = () => {
    // ── STEP 1: Identity ──────────────────────────────────────────────────
    if (currentStep === 'identity') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              Vehicle Identity
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
              Basic details, registration and assignment info.
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            {/* Vehicle Name */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Vehicle Name *</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  {...getFieldProps('vehicleName')}
                  error={Boolean(touched.vehicleName && errors.vehicleName)}
                  helperText={touched.vehicleName && errors.vehicleName}
                />
              </FormControl>
            </Grid>
            {/* Vehicle Type */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(touched.vehicleType && errors.vehicleType)}>
                <InputLabel sx={labelSx}>Vehicle Type *</InputLabel>
                <Select
                  sx={selectSx}
                  label="Vehicle Type *"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('vehicleType')}
                  value={values?.vehicleType}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                >
                  <MenuItem value="truck" sx={{ p: 1.5 }} className={classes.menuItem}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      {LocalShippingOutlinedIcon ? (
                        <LocalShippingOutlinedIcon sx={{ mr: 1 }} />
                      ) : null}
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Truck
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="train" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      {TramOutlinedIcon ? <TramOutlinedIcon sx={{ mr: 1 }} /> : null}
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Train
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="tempo" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      {ElectricRickshawOutlinedIcon ? (
                        <ElectricRickshawOutlinedIcon sx={{ mr: 1 }} />
                      ) : null}
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Tempo
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="ship" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      {SailingOutlinedIcon ? <SailingOutlinedIcon sx={{ mr: 1 }} /> : null}
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Ship
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="container" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                      }}
                    >
                      {LocalShippingIcon ? <LocalShippingIcon sx={{ mr: 1 }} /> : null}
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Container
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
                {touched.vehicleType && errors.vehicleType && (
                  <FormHelperText>{errors.vehicleType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {/* Registration No */}
            {!vehicleData?.id && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <TextField
                    label={<FieldLabel>Registration No *</FieldLabel>}
                    sx={fieldSx}
                    variant="outlined"
                    {...getFieldProps('registrationNo')}
                    error={Boolean(touched.registrationNo && errors.registrationNo)}
                    helperText={touched.registrationNo && errors.registrationNo}
                    onChange={(e) =>
                      formik.setFieldValue('registrationNo', e.target.value.toUpperCase())
                    }
                  />
                </FormControl>
              </Grid>
            )}
            {/* Make */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Make</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  placeholder="e.g. Tata, Ashok Leyland"
                  {...getFieldProps('make')}
                  error={Boolean(touched.make && errors.make)}
                  helperText={touched.make && errors.make}
                />
              </FormControl>
            </Grid>
            {/* Fuel Type */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx} error={Boolean(touched.fuelType && errors.fuelType)}>
                  Fuel Type
                </InputLabel>
                <Select
                  sx={selectSx}
                  label="Fuel Type"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('fuelType')}
                  value={values?.fuelType || ''}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  error={Boolean(touched.fuelType && errors.fuelType)}
                >
                  {FUEL_TYPES.map((f) => (
                    <MenuItem
                      sx={{ textTransform: 'capitalize' }}
                      key={f}
                      value={f}
                      className={classes.menuItem}
                    >
                      <Typography sx={labelSx}>{f}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                {touched.fuelType && errors.fuelType && (
                  <FormHelperText sx={{ color: 'error.main' }}>{errors.fuelType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            {/* Manufacturer Year */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Manufacturer Year</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  type="number"
                  placeholder="e.g. 2021"
                  {...getFieldProps('manufacturerYear')}
                  error={Boolean(touched.manufacturerYear && errors.manufacturerYear)}
                  helperText={touched.manufacturerYear && errors.manufacturerYear}
                />
              </FormControl>
            </Grid>
            {/* Chassis No */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Chassis No *</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  {...getFieldProps('chassisNo')}
                  error={Boolean(touched.chassisNo && errors.chassisNo)}
                  helperText={touched.chassisNo && errors.chassisNo}
                />
              </FormControl>
            </Grid>
            {/* Engine No */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Engine No *</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  {...getFieldProps('engineNo')}
                  error={Boolean(touched.engineNo && errors.engineNo)}
                  helperText={touched.engineNo && errors.engineNo}
                />
              </FormControl>
            </Grid>
            {/* Max Speed */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>Max Speed (km/h) *</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  type="number"
                  {...getFieldProps('vehicleSpeed')}
                  error={Boolean(touched.vehicleSpeed && errors.vehicleSpeed)}
                  helperText={touched.vehicleSpeed && errors.vehicleSpeed}
                />
              </FormControl>
            </Grid>
            {/* Fleet */}
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx} error={Boolean(touched.fleet && errors.fleet)}>
                  Fleet *
                </InputLabel>
                <Select
                  sx={selectSx}
                  label="Fleet *"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('fleet')}
                  value={values?.fleet || ''}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  error={Boolean(touched.fleet && errors.fleet)}
                >
                  {fleets.map((f) => (
                    <MenuItem key={f.id} value={f.id} className={classes.menuItem}>
                      <Typography sx={labelSx}>{f.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                {touched.fleet && errors.fleet && (
                  <FormHelperText sx={{ color: 'error.main' }}>{errors.fleet}</FormHelperText>
                )}
              </FormControl>
            </Grid> */}
            {/* Transporter */}
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx} error={Boolean(touched.transporter && errors.transporter)}>
                  Associated Transporter *
                </InputLabel>
                <Select
                  sx={selectSx}
                  label="Associated Transporter *"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('transporter')}
                  value={values?.transporter || ''}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  error={Boolean(touched.transporter && errors.transporter)}
                >
                  {transporters.map((t) => (
                    <MenuItem key={t.id} value={t.id} className={classes.menuItem}>
                      <Typography sx={labelSx}>{t.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                {touched.transporter && errors.transporter && (
                  <FormHelperText sx={{ color: 'error.main' }}>{errors.transporter}</FormHelperText>
                )}
              </FormControl>
            </Grid> */}
            {/* Assigned Driver */}
            {/* <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel
                  sx={labelSx}
                  error={Boolean(touched.assignedDriver && errors.assignedDriver)}
                >
                  Assign Driver *
                </InputLabel>
                <Select
                  sx={selectSx}
                  label="Assign Driver *"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('assignedDriver')}
                  value={values?.assignedDriver || ''}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  error={Boolean(touched.assignedDriver && errors.assignedDriver)}
                >
                  {drivers.map((d) => (
                    <MenuItem key={d.id} value={d.id} className={classes.menuItem}>
                      <Typography sx={labelSx}>{d.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                {touched.assignedDriver && errors.assignedDriver && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {errors.assignedDriver}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid> */}
            {/* about */}
            <Grid item xs={12} md={12}>
              <FormControl fullWidth>
                <TextField
                  label={<FieldLabel>About</FieldLabel>}
                  sx={fieldSx}
                  variant="outlined"
                  multiline
                  rows={1}
                  {...getFieldProps('vehicleAbout')}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // ── STEP 2: Config ────────────────────────────────────────────────────
    if (currentStep === 'config') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              Vehicle Configuration
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
              Set total load capacity and define individual chambers.
            </Typography>
          </Box>

          {/* Total Capacity */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Voice Box Alert Language</InputLabel>
                <Select
                  sx={selectSx}
                  label="Voice Box Alert Language"
                  IconComponent={ExpandMoreIcon}
                  {...getFieldProps('voiceBoxAlertLanguage')}
                  value={values?.voiceBoxAlertLanguage || ''}
                  MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                >
                  {VOICE_BOX_ALERT_LANGUAGES.map((f) => (
                    <MenuItem
                      sx={{ textTransform: 'capitalize' }}
                      key={f}
                      value={f}
                      className={classes.menuItem}
                    >
                      <Typography sx={labelSx}>{f}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Total Capacity */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={<FieldLabel>Total Load Capacity (tons)</FieldLabel>}
                type="number"
                value={totalCapacity}
                onChange={(e) => {
                  setTotalCapacity(e.target.value);
                  setChamberError('');
                }}
                sx={fieldSx}
                variant="outlined"
              />
            </Grid>

            {/* ✅ Chamber Count Stepper */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={<FieldLabel>No. of Chambers</FieldLabel>}
                type="number"
                inputProps={{ min: 1 }}
                value={chamberCount}
                onChange={(e) => handleChamberCountChange(e.target.value)}
                sx={fieldSx}
                variant="outlined"
              />
            </Grid>

            {/* Chamber Sum */}
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pt: { xs: 0, md: 0.5 }
                }}
              >
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Chambers sum:</Typography>
                <Chip
                  label={`${chamberSum} / ${totalNum || '?'} tons`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    backgroundColor: bgColor,
                    color: textColor,
                    border: `1px solid ${borderColor}`
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Chambers */}
          <Grid container spacing={1.5}>
            {chambers.map((chamber, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#fafafa',
                    position: 'relative'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.25
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                        Chamber {idx + 1}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={<FieldLabel>Load Capacity (tons)</FieldLabel>}
                        type="number"
                        value={chamber.loadCapacity}
                        onChange={(e) => updateChamber(idx, 'loadCapacity', e.target.value)}
                        sx={fieldSx}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={labelSx}>Load Type</InputLabel>
                        <Select
                          sx={selectSx}
                          label="Load Type"
                          value={chamber.loadType}
                          onChange={(e) => updateChamber(idx, 'loadType', e.target.value)}
                          IconComponent={ExpandMoreIcon}
                          MenuProps={{
                            PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } }
                          }}
                        >
                          {LOAD_TYPES.map((lt) => (
                            <MenuItem key={lt} value={lt} className={classes.menuItem}>
                              <Typography sx={labelSx}>{lt}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>

          {chamberError && (
            <Typography sx={{ fontSize: 12, color: '#dc2626', mt: -0.5 }}>
              {chamberError}
            </Typography>
          )}
        </Box>
      );
    }

    // ── STEP 3: Compliance ────────────────────────────────────────────────
    if (currentStep === 'compliance') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              Compliance Documents
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
              Certificate details are stored locally and not submitted with the vehicle record.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CertCard
                title="Fitness Certificate"
                fields={[
                  { key: 'number', placeholder: 'Number' },
                  { key: 'expiry', placeholder: 'Expiry Date', type: 'date' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CertCard
                title="PESO Licence"
                fields={[
                  { key: 'number', placeholder: 'PESO Number' },
                  { key: 'expiry', placeholder: 'Expiry Date', type: 'date' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CertCard
                title="Insurance Policy"
                fields={[
                  { key: 'policyNo', placeholder: 'Policy Number' },
                  { key: 'provider', placeholder: 'Provider (e.g. New India)' },
                  { key: 'expiry', placeholder: 'Expiry Date', type: 'date' }
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CertCard
                title="National Permit"
                fields={[
                  { key: 'permitNo', placeholder: 'Permit Number' },
                  { key: 'expiry', placeholder: 'Expiry Date', type: 'date' }
                ]}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    // ── STEP 4: Hardware ──────────────────────────────────────────────────
    if (currentStep === 'hardware') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              Hardware Devices
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
              Assign GPS tracker and dashcam AI device to this vehicle.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {/* GPS Device */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  background: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#64748b'
                  }}
                >
                  GPS Device
                </Typography>
                <FormControl fullWidth>
                  <InputLabel sx={labelSx}>Select GPS Device</InputLabel>
                  <Select
                    sx={selectSx}
                    label="Select GPS Device"
                    value={gpsDevice}
                    onChange={(e) => setGpsDevice(e.target.value)}
                    IconComponent={ExpandMoreIcon}
                    MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  >
                    {normalizedGpsDevices.map((d) => (
                      <MenuItem key={d.id} value={d.id} className={classes.menuItem}>
                        <Typography sx={labelSx}>{d.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {gpsDevice && (
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe'
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>
                      Selected: {normalizedGpsDevices.find((d) => d.id === gpsDevice)?.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Dashcam AI Device */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  background: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#64748b'
                  }}
                >
                  Dashcam AI Device
                </Typography>
                <FormControl fullWidth>
                  <InputLabel sx={labelSx}>Select Dashcam Device</InputLabel>
                  <Select
                    sx={selectSx}
                    label="Select Dashcam Device"
                    value={dashcamDevice}
                    onChange={(e) => setDashcamDevice(e.target.value)}
                    IconComponent={ExpandMoreIcon}
                    MenuProps={{ PaperProps: { style: { maxHeight: 180, overflowY: 'auto' } } }}
                  >
                    {normalizedDashcamDevices.map((d) => (
                      <MenuItem key={d.id} value={d.id} className={classes.menuItem}>
                        <Typography sx={labelSx}>{d.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {dashcamDevice && (
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe'
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>
                      Selected: {normalizedDashcamDevices.find((d) => d.id === dashcamDevice)?.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={alertMetas.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert variant={alertMetas.variant} severity={alertMetas.severity}>
          {alertMetas.msg}
        </Alert>
      </Snackbar>

      <Modal
        open={openAssignedModal}
        onClose={handleClose}
        aria-labelledby="add-asset-modal"
        data-testId="add-vehicle-modal"
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              {/* Header */}
              <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 900, mb: 2 }}>
                {vehicleData?.id ? 'Update Vehicle' : 'Add New Vehicle'}
              </Typography>

              {/* Timeline */}
              <AssetTimeline currentStep={currentStep} />

              {/* Step Content */}
              <Box>{renderStepContent()}</Box>

              {/* Footer */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #f3f4f6'
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={currentStep === 'identity' ? handleClose : handleBack}
                >
                  {currentStep === 'identity' ? 'Cancel' : 'Back'}
                </Button>

                {currentStep !== 'hardware' ? (
                  <Button variant="contained" size="small" onClick={handleNext}>
                    Next
                  </Button>
                ) : (
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    size="small"
                    data-testid="add-or-save-button"
                    loading={isSubmitting}
                  >
                    {vehicleData?.id ? 'Save Changes' : 'Add Vehicle'}
                  </LoadingButton>
                )}
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
    </Box>
  );
}
